import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import './DeploymentStatusBox.css';
const POLL_INTERVAL = 30000; // 30 seconds
export const DeploymentStatusBox = () => {
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [stale, setStale] = useState(false);
    const currentShaRef = useRef(undefined);
    // Fetch deployment info
    const fetchDeploymentInfo = async () => {
        try {
            const res = await fetch('/deployment-info.json', { cache: 'no-store' });
            if (!res.ok)
                throw new Error('Not found');
            const data = await res.json();
            if (data.commit_sha && data.github_repo) {
                // If we already have info, check for update
                if (currentShaRef.current &&
                    data.commit_sha !== currentShaRef.current) {
                    setUpdateAvailable(true);
                    setStale(true);
                    // Do NOT update info, keep showing the old info until reload
                    return;
                }
                setInfo(data);
                currentShaRef.current = data.commit_sha;
            }
            else {
                setError(true);
            }
        }
        catch {
            setError(true);
        }
    };
    useEffect(() => {
        fetchDeploymentInfo();
        const interval = setInterval(fetchDeploymentInfo, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, []);
    if (error || !info)
        return null;
    const { commit_sha, commit_date, branch, pr_number, github_repo, pr_title, commits, } = info;
    const shortSha = commit_sha?.slice(0, 7);
    const commitUrl = github_repo && commit_sha
        ? `https://github.com/${github_repo}/commit/${commit_sha}`
        : undefined;
    const prUrl = github_repo && pr_number
        ? `https://github.com/${github_repo}/pull/${pr_number}`
        : undefined;
    const formattedDate = commit_date
        ? new Date(commit_date).toLocaleString()
        : '';
    // Tooltip for PR number: PR title only
    const getPrTooltip = () => pr_title || '';
    // Tooltip for commit SHA: full commit message for current commit
    const getCommitTooltip = () => {
        if (info && info.commit_message_full)
            return info.commit_message_full;
        if (commit_sha && commits) {
            const found = commits.find((c) => c.sha === commit_sha);
            if (found && found.message)
                return found.message;
        }
        return '';
    };
    // Tooltip for timestamp: deployed_by and up to 5 short commit messages
    const getTimestampTooltip = () => {
        let tooltip = '';
        if (info && info.deployed_by) {
            tooltip += `Deployed by: ${info.deployed_by}\n`;
        }
        if (commits && commits.length > 0) {
            tooltip += commits
                .slice(0, 5)
                .map((c) => `- ${c.message}`)
                .join('\n');
        }
        return tooltip.trim();
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: `deployment-status-box${stale ? ' stale' : ''}`, children: [pr_number && prUrl && (_jsxs("div", { children: [_jsx("strong", { children: "PR:" }), ' ', _jsxs("a", { href: prUrl, target: "_blank", rel: "noopener noreferrer", title: getPrTooltip(), children: ["#", pr_number] })] })), !pr_number && branch && (_jsxs("div", { children: [_jsx("strong", { children: "Branch:" }), " ", branch] })), commit_sha && commitUrl && (_jsxs("div", { children: [_jsx("strong", { children: "Commit:" }), ' ', _jsx("a", { href: commitUrl, target: "_blank", rel: "noopener noreferrer", title: getCommitTooltip(), children: shortSha })] })), _jsx("div", { className: "deployment-timestamp", title: getTimestampTooltip(), children: formattedDate }), stale && (_jsx("div", { className: "deployment-stale-indicator", title: "This page is outdated", children: "Outdated" }))] }), updateAvailable && (_jsxs("div", { className: "deployment-update-banner", children: [_jsx("span", { children: "New version available." }), _jsx("button", { className: "reload-btn", onClick: () => window.location.reload(), children: "Reload" })] }))] }));
};
